import { useParams } from "react-router-dom";
import EventEmployeesTable from "@/components/events/EventEmployeesTable";
import EventHeader from "@/components/events/EventHeader";
import EventInformation from "@/components/events/EventInformation";
import EventPositionsSummary from "@/components/events/EventPositionsSummary";
import EventWaitlistJoin from "@/components/events/EventWaitlistJoin";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useQuery } from "@tanstack/react-query";
import { Loading } from "@/components/ui/loading";
import { toast } from "@/components/ui/use-toast";
import { SpotlightCard } from "@/components/brand/SpotlightCard";
import { BackLink } from "@/components/brand/BackLink";
import { StatStrip, Stat } from "@/components/brand/StatStrip";
import { formatPay } from "@/utils/money";
import { format } from "date-fns";

interface UserEventDetail {
  eventId: string;
  companyName: string;
  startTime: string;
  eventLength: number;
  address: string;
  city: string;
  nameOfLocation: string;
  eventPositions: EventPosition[];
  totalPositions: number;
  filledPositions: number;
  deliveryInfo: string;
  dealerSpecialInstructions: string;
  extraPayPerDealer: number;
  setupTime: string;
  parkingInfo: string;
  hasWaitlist: boolean;
  maxWaitlistSize: number | null;
  currentWaitlistSize: number | null;
  userOnWaitlist: boolean;
}

export interface EventPosition {
  positionId: string;
  eventPositionId: string;
  tableName: string;
  employeesRequired: number;
  quantity: number;
  userEligible: boolean;
  employeePay: number;
  isFlatRate?: boolean;
  allowsMultipleAssignments: boolean;
  signedUpUsers: SignedUpUser[];
}

export interface SignedUpUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface UserEventPayDTO {
  eventId: string;
  payAmount: number;
}

export interface FormattedEvent {
  title: string;
  /** The full date, spelled out. Kept for anything that wants it in prose. */
  date: string;
  /** Weekday · venue · city, under the title. */
  subtitle: string;
  /** The date as a figure, for the stat strip. */
  shortDate: string;
  time: string;
  location: string;
  city: string;
  nameOfLocation: string;
  specialInstructions: string;
  positions: EventPosition[];
  deliveryInfo: string;
  extraPayPerDealer: number;
  setupTime: string;
  parkingInfo: string;
}

/**
 * Whether the "Your pay" cell appears at all.
 *
 * The undefined case is deliberate and pinned by a test (SPEC 11.14): when the
 * pay lookup fails, `userPay` is undefined, so `earned` is undefined rather
 * than false and the cell shows $0 rather than disappearing.
 */
function payCellIsShown(
  loading: boolean,
  pay: UserEventPayDTO | undefined,
): boolean {
  const earned = !loading && pay && pay.payAmount > 0;
  return earned === undefined ? true : Boolean(earned);
}

export default function EventDetails() {
  const { id } = useParams();

  const {
    data: event,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["event", id],
    queryFn: async () => {
      const response = await axiosInstance.get<UserEventDetail>(
        `userevent/${id}`,
      );
      return response.data;
    },
    meta: {
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to load event details",
          variant: "destructive",
        });
      },
    },
  });

  const { data: userPay, isLoading: userPayLoading } = useQuery({
    queryKey: ["userPay", id],
    queryFn: async () => {
      const response = await axiosInstance.get<UserEventPayDTO>(
        `userpay/${id}`,
      );
      return response.data;
    },
    enabled: !!id,
    meta: {
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to load pay information",
          variant: "destructive",
        });
      },
    },
  });

  if (isLoading) return <Loading />;

  if (error || !event) {
    return <div>Event not found</div>;
  }

  const eventDate = new Date(event.startTime);
  const eventEndTime = new Date(
    eventDate.getTime() + event.eventLength * 60 * 60 * 1000,
  );

  const formattedEvent: FormattedEvent = {
    title: event.companyName,
    date: format(eventDate, "EEEE, MMMM d, yyyy"),
    subtitle: [format(eventDate, "EEEE"), event.nameOfLocation, event.city]
      .filter(Boolean)
      .join(" · "),
    shortDate: format(eventDate, "MMM d, yyyy"),
    // "7:00 - 11:00 PM", not "7:00 PM - 11:00 PM": the meridiem is only
    // worth stating twice when the event crosses noon or midnight.
    time:
      format(eventDate, "a") === format(eventEndTime, "a")
        ? `${format(eventDate, "h:mm")} - ${format(eventEndTime, "h:mm a")}`
        : `${format(eventDate, "h:mm a")} - ${format(eventEndTime, "h:mm a")}`,
    location: event.address,
    city: event.city,
    nameOfLocation: event.nameOfLocation,
    specialInstructions: event.dealerSpecialInstructions,
    positions: event.eventPositions,
    deliveryInfo: event.deliveryInfo,
    extraPayPerDealer: event.extraPayPerDealer,
    setupTime: event.setupTime,
    parkingInfo: event.parkingInfo,
  };

  const showPay = payCellIsShown(userPayLoading, userPay);

  const stats: Stat[] = [
    { label: "Date", value: formattedEvent.shortDate },
    { label: "Time", value: formattedEvent.time },
    ...(showPay
      ? [
          {
            label: "Your pay",
            value: formatPay(userPay?.payAmount || 0),
            highlight: true,
          },
        ]
      : []),
    {
      label: "Positions",
      value: `${event.filledPositions}/${event.totalPositions}`,
    },
  ];

  return (
    // `relative` is what the dot grid's absolutely positioned pseudo-element
    // hangs off.
    <div className="dot-grid relative min-h-screen bg-background p-6 animate-fade-in">
      {/* Also relative, or the texture paints over the content: an absolutely
          positioned pseudo-element outranks static siblings. */}
      <div className="relative max-w-5xl mx-auto space-y-8">
        <BackLink to="/">Back to Events</BackLink>

        <SpotlightCard className="overflow-hidden border-border p-0">
          <EventHeader event={formattedEvent} />
          {/*
            The headline figures, so what someone opened the page for is
            readable without reading: when it is, how long, and what it pays.
            "Your pay" is the highlighted cell - it is the thing people come
            here to check, and it used to be a line of body text with an emoji
            on it, below the fold.
          */}
          <StatStrip stats={stats} />
          <EventInformation event={formattedEvent} />
          <div className="divide-y divide-border">
            <EventPositionsSummary
              eventPositions={formattedEvent.positions}
              eventId={event.eventId}
            />
            <EventEmployeesTable eventPositions={formattedEvent.positions} />
          </div>
        </SpotlightCard>

        <EventWaitlistJoin
          eventId={event.eventId}
          hasWaitlist={event.hasWaitlist}
          maxWaitlistSize={event.maxWaitlistSize}
          currentWaitlistSize={event.currentWaitlistSize}
          userOnWaitlist={event.userOnWaitlist}
        />
      </div>
    </div>
  );
}
